import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { DynamicForm } from "../../dynamicForm";
import { updateDpiaCollaboratorsMutationOptions } from "../../api/dpia";
import type { DpiaById } from "../../api/dpia.types";
import { useGetUsersFromCurrentOrganisation } from "../../api/users";
import { userToAutocompleteOption } from "../../shared/Autocomplete";
import { PersonIcon3 } from "../../shared/icons";
import { useUserInfo } from "../../api/userInfo";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";

type DpiaCollaboratorsProps = { dpia: DpiaById; isReadOnly: boolean };

const MAX_INVITES_PER_SAVE = 10;

export const DpiaCollaboratorsSelect = ({ dpia, isReadOnly }: DpiaCollaboratorsProps) => {
  const { navigate, paths } = usePrivacyNavigate();
  const updateCollaboratorsMutation = useMutation(updateDpiaCollaboratorsMutationOptions());
  const userInfo = useUserInfo();
  const [wasHovered, setWasHovered] = useState(false);
  const usersQuery = useGetUsersFromCurrentOrganisation({ enabled: !isReadOnly && wasHovered });

  const loggedInUserIsCollaborator = dpia.collaborators?.some(({ email }) => email === userInfo.email);
  const loggedInUserIsResponsible = dpia.responsible?.email === userInfo.email;

  return (
    <DynamicForm.InlineEntityMultiSelect
      id="dpia-collaborators"
      isReadOnly={isReadOnly}
      value={(dpia.collaborators ?? []).map(userToAutocompleteOption)}
      options={usersQuery.data?.users.map(userToAutocompleteOption)}
      onHover={() => setWasHovered(true)}
      saveAfterAdditions={MAX_INVITES_PER_SAVE}
      renderIcon={iconProps => <PersonIcon3 {...iconProps} />}
      noOptionsTextKey="DynamicForm.PersonSelect.Empty"
      onSave={async selected => {
        const loggedInUserLosesAccess =
          loggedInUserIsCollaborator &&
          !loggedInUserIsResponsible &&
          !selected.some(({ description }) => description === userInfo.email);
        await updateCollaboratorsMutation.mutateAsync({
          dpiaId: dpia.id,
          userIds: selected.map(({ value }) => value),
          loggedInUserLosesAccess,
        });
        if (loggedInUserLosesAccess) navigate(paths.dpiaGrid());
      }}
    />
  );
};

export const DpiaCollaborators = (props: DpiaCollaboratorsProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Typography variant="h6">{t("DpiaDrawer.Collaborators.Label")}</Typography>
      <Box sx={{ marginLeft: "-12px", display: "flex", alignItems: "center" }}>
        <DpiaCollaboratorsSelect {...props} />
      </Box>
    </>
  );
};
